import Log, {
  AccountActionEvents,
  LogEventCategory,
} from "../api/entities/Log";

// #856 / WS-12: members lose their web session 10–38 s after an app→web
// handoff and nothing records which logout() fired. Every session clear (and
// every WS-01 skip) queues one `web-logout` event here.
//
// Why the queue and not an immediate send: /logs requires auth, and at this
// moment the tokens are being removed — an immediate request would 401 like
// the unattributable /logs lines in the 2026-09-04 incident. The ff-log-*
// queue survives logout() (which removes only ff-at/ff-rt) and LoggerContext
// flushes it on the next signed-in session — in the WS-12 pattern, the
// member's retry. If they never sign in again in this browser, it is lost.
//
// PII: no email, phone, token or full URL — the handoff URL carries a
// token, so only the first path segment is recorded.

let sessionStoredAt: number | undefined;

// Two events can be queued in the same millisecond (a failed refresh's
// api-unauthorized, then the enclosing validateLogin catch). A per-call
// sequence keeps both keys distinct so neither overwrites the other; the
// flusher (LoggerContext) only matches the ff-log- prefix.
let queueSequence = 0;

// Called whenever a login flow stores a new session, so the event can say how
// long after sign-in the clear happened (the WS-12 signature).
export const markSessionStored = (): void => {
  sessionStoredAt = Date.now();
};

export const queueWebLogoutEvent = (
  reason: string,
  outcome: "cleared" | "skipped",
  tokens: { hadAccessToken: boolean; hadRefreshToken: boolean },
): void => {
  try {
    const now = Date.now();
    const route =
      typeof window !== "undefined"
        ? window.location.pathname.split("/")[1] || "/"
        : "";
    const log: Log = {
      category: LogEventCategory.ACCOUNT_ACTION,
      event: AccountActionEvents.WEB_LOGOUT,
      data: {
        reason,
        outcome,
        route,
        hadAccessToken: tokens.hadAccessToken,
        hadRefreshToken: tokens.hadRefreshToken,
        msSincePageLoad:
          typeof performance !== "undefined"
            ? Math.round(performance.now())
            : undefined,
        msSinceSessionStored:
          sessionStoredAt !== undefined ? now - sessionStoredAt : undefined,
      },
      timestamp: new Date(now),
    };
    localStorage.setItem(
      `ff-log-${now}-${queueSequence++}-${AccountActionEvents.WEB_LOGOUT}`,
      JSON.stringify(log),
    );
  } catch (e) {
    // Telemetry must never break a sign-out; the console tag in
    // AccountContext still fires.
    console.log("[web-logout] telemetry queue failed:", String(e));
  }
};
